// Enhets-verktøy: plasser, flytt (dra), gi nytt navn og slett APP-6-enheter på
// kartet — ATAK-kjernefunksjonen «legg en enhet på situasjonsbildet». Enhetene
// rendres i et eget symbol-lag; onChange() lar main dele dem i sanntid.
import maplibregl, { type Map as MlMap, GeoJSONSource, type MapMouseEvent } from 'maplibre-gl';
import { featureCollection, formatMgrs, latLonToMgrs, type GeoFeature, type Track } from '@skade/contracts';
import { AFFIL_LABEL, ECHELONS, parseSidc, typeForSidc } from './app6.js';
import { ensureSymbol } from '../symbols.js';
import type { Kartmodus } from '../map/kartmodus.js';
import { naarKartetErKlart } from '../map/verktoylag.js';

export const UNIT_PLACE_ID = 'place-unit';

/**
 * Kartmodus for FLYTTING av en enhet ved klikk.
 *
 * HVORFOR DEN FINNES: flytting fantes bare som museDRAG. Det ga to hull. (1) På
 * nettbrett kunne en enhet verken velges eller flyttes i det hele tatt — det
 * eneste kartlaget lyttet på `mousedown` (funn 9 i UX-rapporten). (2) Objektlista
 * og detaljpanelet kunne ikke TILBY «flytt», bare beskrive at man kan dra i
 * kartet — og en erklært evne uten en handling er nettopp den feilklassen
 * registeret skal gjøre umulig.
 */
export const UNIT_MOVE_ID = 'unit-move';

/**
 * Hvor langt en enhet må dras (skjermpiksler) før det regnes som en FORFLYTNING.
 *
 * ⚠ Uten denne terskelen var ETT KLIKK på en enhet nok til å utløse en
 * fempunktsordre for forflytning: `moved` ble satt av den FØRSTE `mousemove`, og
 * en hånd som holder en mus rører seg alltid minst én piksel. Utfallet var
 * dermed tilfeldig — noen ganger fikk operatøren enhetskortet, andre ganger en
 * ferdig utfylt forflytningsordre han aldri ba om. En ordre er en konsekvent
 * handling og skal kreve en utvetydig håndbevegelse.
 */
export const FLYTT_TERSKEL_PX = 8;

/**
 * Fast id for EGEN posisjon (BFT). Eksportert fordi flere steder må kunne kjenne
 * igjen markøren og behandle den som en MÅLING, ikke som en enhet operatøren har
 * plassert: den kan ikke gis nytt navn, ikke få nytt symbol og ikke få ordre.
 */
export const BFT_SELF_ID = 'bft:self';

/**
 * Når en BFT-måling regnes som GAMMEL og skal si det på kartet.
 *
 * 60 s: egen GPS gir normalt fiks hvert sekund, så ett minutt uten ny måling
 * betyr at noe faktisk er galt (skjermen sover, GPS mistet sikt, batterisparing).
 * Kortere ga falske «gammel»-varsler i bygg og tunnelinnkjøring; lengre gjorde at
 * markøren kunne stå minutter etter at posisjonen sluttet å oppdatere seg.
 */
export const BFT_GAMMEL_MS = 60_000;

export interface PlacedUnit {
  id: string;
  sidc: string;
  lon: number;
  lat: number;
  label: string;
  /**
   * Når posisjonen ble MÅLT (BFT/GPS). Udefinert for enheter en operatør har
   * plassert manuelt — de har ingen måletid, bare en plassering.
   *
   * ⚠ Grunnloven krever at alder på en måling ALLTID vises. Feltet finnes for at
   * alderen skal kunne regnes ut der markøren vises (kart, objektliste, popup),
   * i stedet for at en gammel posisjon presenteres som fersk.
   */
  tsMs?: number;
  /**
   * Når EN OPERATØR sist endret enheten (plassert, flyttet, døpt om, nytt
   * symbol). Skilles fra `tsMs` (måletid, kun BFT): dette er redigeringstid, og
   * finnes for at objektpanelet skal kunne vise «sist endret» (UX-funn 3/5) —
   * en enhet uten alder på siste endring kan ikke vurderes mot situasjonen nå.
   */
  endretMs?: number;
}

let seq = 0;

/** Kompakt alder: «12 s», «4 min», «1 t 05 min». Ren funksjon → testbar. */
export function alderTekst(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} t ${String(m % 60).padStart(2, '0')} min`;
}

/**
 * Én linje som forteller BÅDE når posisjonen ble målt og hvor gammel målingen er.
 * Zulu-tid fordi det er tiden operativt personell fører logg i; alderen fordi et
 * klokkeslett alene krever hoderegning i en situasjon der ingen har tid til det.
 */
export function maalingsLinje(tsMs: number, nowMs: number): string {
  const dtg = new Date(tsMs).toISOString().slice(11, 19) + 'Z';
  return `målt ${dtg} · ${alderTekst(nowMs - tsMs)} gammel`;
}

/**
 * Undertittel for en GNÅ-bruker i objektlista (kortet «Batteri og hastighet
 * per bruker i dispatch-kartet»/DISP-02): grad, sist-aktiv (alder på siste
 * posisjonsrapport — ALLTID vist, grunnloven), og fart NÅR telefonen faktisk
 * har GPS-fart å vise.
 *
 * Fart leses fra `t.extra['Fart']` — samme streng hubben allerede regner ut i
 * `posisjonTilSpor` (@skade/contracts/gna-posisjon.ts). Ingen ny
 * m/s→km/t-omregning her: grunnlov §3, ett sted regner det ut, denne gjenbruker.
 *
 * ⚠ BATTERI VISES BEVISST IKKE HER. `GnaPosisjonSchema.batteriProsent` er et
 * gyldig, valgfritt kontraktsfelt, og hubben setter `extra['Batteri']` når det
 * finnes (vises allerede i detaljpanelet, se `sporRader` i `ui/shell.ts`) —
 * men verifisert 2026-08-20 (DISP-02): INGEN GNÅ-klient kaller noe OS-batteri-
 * API noe sted i `lib/`. Feltet er fullt kontrakts- og hub-wiret, men kommer
 * aldri fylt ut fra en ekte telefon i dag. Et batteriikon i lista ville bedt
 * operatøren stole på et tall som aldri kommer (grunnlov §1) — det krever en
 * klientside GNÅ-endring (Flutter) som er utenfor denne leveransen.
 */
export function gnaRosterSub(t: Track, naa: number, anomNote?: string): string {
  const grad = t.person?.grad?.trim();
  const fart = t.extra?.['Fart'];
  return `GNÅ${grad ? ` · ${grad}` : ''} · ${maalingsLinje(t.tsMs || naa, naa)}` +
    (fart ? ` · ${fart}` : '') +
    (anomNote ? ` · ⚠ ${anomNote}` : '');
}

/**
 * Etiketten som faktisk tegnes på kartet.
 *
 * ⚠ HVA SOM GIKK GALT: BFT-markøren sto med bare kallesignalet som navn, akkurat
 * som en manuelt plassert enhet. Sluttet GPS-en å levere, sto den likevel der som
 * en helt vanlig, tilsynelatende fersk enhet — en løgn om ferskhet. Nå roper
 * etiketten når målingen er eldre enn `BFT_GAMMEL_MS`. Fersk måling får INGEN
 * suffiks: skulle den alltid vist sekunder, ville teksten flimret ved hver fiks
 * og gjort kartet urolig uten å tilføre noe.
 */
export function kartEtikett(u: PlacedUnit, nowMs: number): string {
  if (u.tsMs === undefined) return u.label;
  const alder = nowMs - u.tsMs;
  return alder >= BFT_GAMMEL_MS ? `${u.label} ⚠ ${alderTekst(alder)} GAMMEL` : u.label;
}

// ── Objektpanelet: hva et enhetsvalg skal VISE (UX-funn 3/5) ─────────────────

/** ORBAT-oppslaget slik `orbatOppslag` leverer det (nivå + kommandokjede). */
export interface OrbatInfo {
  navn: string;
  echelonNavn: string;
  stiTekst: string;
}

export interface EnhetDetaljer {
  tittel: string;
  undertittel: string;
  rader: Array<[string, string]>;
}

/**
 * Innholdet i høyrepanelet når en enhet velges (kartklikk ELLER objektliste).
 *
 * ⚠ HVA SOM GIKK GALT (funn 3/5): et symbolklikk markerte bare symbolet — det
 * faste detaljpanelet sto igjen på «Velg et objekt på kartet», og eneste
 * informasjon var en flyktig boble som dekket kartet. Panelet er flata som er
 * BYGGET for å lese et objekt; boblen beholdes som hurtigredigering.
 *
 * Ren funksjon (ingen DOM, tida sendes inn) → testbar i vitest uten jsdom.
 * VISNING og HANDLING skilles med vilje: radene her er «se»; knappene (Endre /
 * Gi ordre / Ta av kartet) legges på av kalleren som eier handlingene.
 */
export function enhetDetaljer(u: PlacedUnit, orbat: OrbatInfo | null, nowMs: number): EnhetDetaljer {
  // BFT-markøren er en MÅLING, ikke en enhet — panelet sier det samme som
  // popup-en: kilde og alder, aldri redigeringsfelter.
  if (u.id === BFT_SELF_ID) {
    return {
      tittel: u.label,
      undertittel: 'EGEN POSISJON (BFT) — måling fra GPS',
      rader: [
        ['Posisjon (MGRS)', posisjonsTekst(u)],
        ['Måling', u.tsMs === undefined ? 'måletidspunkt ukjent' : maalingsLinje(u.tsMs, nowMs)],
      ],
    };
  }
  const deler = parseSidc(u.sidc);
  const type = typeForSidc(u.sidc);
  const echelon = ECHELONS.find((e) => e.code === deler.echelon && e.code !== '-');
  const typeTekst = [type?.label ?? 'ukjent type', echelon?.label, AFFIL_LABEL[deler.aff]]
    .filter((s): s is string => !!s).join(' · ');
  return {
    tittel: u.label,
    undertittel: orbat ? `ENHET · ${orbat.echelonNavn}` : 'ENHET · løs markering',
    rader: [
      ['Type/symbol', `${typeTekst} (SIDC ${u.sidc})`],
      ['Posisjon (MGRS)', posisjonsTekst(u)],
      // ORBAT-kjeden OPPOVER når enheten er koblet — og et ærlig «ikke koblet»
      // når den ikke er det, i stedet for en tom rad som ser ut som en feil.
      ['ORBAT', orbat ? `${orbat.echelonNavn} · ${orbat.stiTekst}` : 'Ikke koblet (løs markering) — plasser fra ORBAT-velgeren for kobling'],
      ['Sist endret', u.endretMs === undefined
        ? 'ukjent (plassert før sporing av endringer)'
        : `${new Date(u.endretMs).toISOString().slice(11, 19)}Z · ${alderTekst(nowMs - u.endretMs)} siden`],
    ],
  };
}

/** MGRS for visning; utenfor sonene faller vi ærlig tilbake til grader. */
function posisjonsTekst(u: PlacedUnit): string {
  const mgrs = latLonToMgrs({ lat: u.lat, lon: u.lon });
  return mgrs ? formatMgrs(mgrs) : `${u.lat.toFixed(5)}, ${u.lon.toFixed(5)} (utenfor MGRS-sonene)`;
}

export class UnitTool {
  private units = new Map<string, PlacedUnit>();
  private pending: { sidc: string; label: string; orbatId?: string } | null = null;
  /** Enheten som venter på et nytt punkt (klikk-flytting). */
  private pendingFlytt: string | null = null;
  private dragging: string | null = null;
  private moved = false;
  private ready = false;
  private popup?: maplibregl.Popup;

  /** Skjermpunkt der draget startet — grunnlaget for terskelen i `onUp`. */
  private dragStartPx: { x: number; y: number } | null = null;

  constructor(
    private map: MlMap,
    private onChange: () => void = () => {},
    private modus?: Kartmodus,
  ) {
    naarKartetErKlart(map, () => this.init());
    this.modus?.registrer({ id: UNIT_PLACE_ID, navn: 'PLASSER ENHET', stopp: () => this.disarm() });
    this.modus?.registrer({
      id: UNIT_MOVE_ID, navn: 'FLYTT ENHET', stopp: () => this.avbrytFlytting(),
    });
  }

  /**
   * Eier et ANNET verktøy kartet nå? Da skal enhetslaget ikke svare på klikk.
   *
   * ⚠ Denne SKAL gå via `eierKlikk()`, ikke via `aktiv`-id-en alene: den nøytrale
   * standardeieren («velg») ER aktiv til vanlig, men eier ikke klikkene. Sammen-
   * ligner man bare id-er, blir enhetene udragbare i det øyeblikket «velg» blir
   * standardtilstanden — altså hele tiden.
   */
  private annenEier(): boolean {
    const a = this.modus?.aktiv ?? null;
    if (a === null || a === UNIT_PLACE_ID || a === UNIT_MOVE_ID) return false;
    return this.modus?.eierKlikk() === true;
  }

  private init() {
    if (this.map.getSource('units')) return;
    this.map.addSource('units', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('units-shared', { type: 'geojson', data: featureCollection([]) });
    // Andres delte enheter (litt dempet, under egne).
    this.map.addLayer({
      id: 'units-shared', type: 'symbol', source: 'units-shared',
      // Echelon-symboler dobbelt størrelse (Dans ønske) — se også 'units' + symbols.ts.
      layout: { 'icon-image': ['get', 'sidc'], 'icon-size': 0.85, 'icon-allow-overlap': true,
        'text-field': ['get', 'label'], 'text-font': ['Open Sans Regular'], 'text-size': 10,
        'text-offset': [0, 2.1], 'text-anchor': 'top', 'text-optional': true },
      paint: { 'icon-opacity': 0.85, 'text-color': '#adbdad', 'text-halo-color': '#0a0c0a', 'text-halo-width': 1.4 },
    });
    this.map.addLayer({
      id: 'units', type: 'symbol', source: 'units',
      // Echelon-symboler dobbelt størrelse (Dans ønske).
      layout: { 'icon-image': ['get', 'sidc'], 'icon-size': 0.99, 'icon-allow-overlap': true,
        'text-field': ['get', 'label'], 'text-font': ['Open Sans Regular'], 'text-size': 11,
        'text-offset': [0, 2.2], 'text-anchor': 'top', 'text-optional': true },
      paint: { 'text-color': '#e6eee6', 'text-halo-color': '#0a0c0a', 'text-halo-width': 1.5 },
    });

    this.map.on('click', this.onMapClick);
    this.map.on('mousedown', 'units', this.onDown);
    // Pekeren eies av det aktive verktøyet: under en måling skal trådkorset ikke
    // bli en flyttepeker fordi musa passerte et enhetssymbol.
    this.map.on('mouseenter', 'units', () => { if (!this.pending && !this.annenEier()) this.map.getCanvas().style.cursor = 'move'; });
    this.map.on('mouseleave', 'units', () => { if (!this.pending && !this.dragging && !this.annenEier()) this.map.getCanvas().style.cursor = ''; });
    this.ready = true;
    this.render();
  }

  /**
   * Væpne plassering: neste kartklikk slipper en enhet med denne SIDC-en.
   *
   * `orbatId` bæres HELT fram til klikket. Uten det ville ORBAT-velgeren bare
   * virket fra høyreklikk (der punktet er kjent på forhånd): armerte man en
   * ORBAT-enhet og klikket på kartet, mistet enheten koblingen og ble en løs
   * markering — nettopp den feilen fase 2 skal fjerne.
   */
  arm(sidc: string, label: string, orbatId?: string) {
    // ⚠ ROTÅRSAK TIL «feil enhetstype på feil sted»: `pending` var en global
    // ventetilstand ingen eide. Armerte man en enhet og så gjorde noe HELT annet
    // på kartet — målte en avstand, tegnet en siktelinje — ble den armerte
    // enheten sluppet ned på DET klikket, uten at noen ba om det. Senere, når
    // operatøren armerte en ny type og klikket, sto den gamle enheten der fra
    // før, på et sted han aldri hadde valgt. Plassering er nå en KARTMODUS:
    // starter du noe annet, forsvinner armeringen.
    this.modus?.taOver(UNIT_PLACE_ID);
    this.pending = { sidc, label, orbatId };
    this.map.getCanvas().style.cursor = 'crosshair';
  }
  disarm() {
    const varArmert = this.pending !== null;
    this.pending = null;
    this.map.getCanvas().style.cursor = '';
    if (varArmert) this.modus?.frigi(UNIT_PLACE_ID);
  }

  /** Er en enhet armert for plassering? (til modus-stripa og test) */
  get erArmert(): boolean { return this.pending !== null; }

  /**
   * Væpne FLYTTING: neste kartklikk flytter enheten dit.
   *
   * Returnerer `false` når flyttingen ikke kan tilbys — ukjent enhet, eller
   * BFT-markøren. ⚠ Egen GPS-posisjon er en MÅLING: å tilby en flytting av den
   * og så nekte ved klikket ville vært en knapp som lyver. Den nektes her, og
   * `evner.flyttbar` for egenposisjon er `false` i registeret.
   */
  armFlytting(id: string): boolean {
    if (id === BFT_SELF_ID) return false;
    if (!this.units.has(id)) return false;
    this.modus?.taOver(UNIT_MOVE_ID);
    this.pendingFlytt = id;
    try { this.map.getCanvas().style.cursor = 'crosshair'; } catch { /* kart uten canvas */ }
    return true;
  }

  /** Avbryt en armert flytting (Escape, eller et annet verktøy tar over). */
  avbrytFlytting(): void {
    const var_ = this.pendingFlytt !== null;
    this.pendingFlytt = null;
    try { this.map.getCanvas().style.cursor = ''; } catch { /* kart uten canvas */ }
    if (var_) this.modus?.frigi(UNIT_MOVE_ID);
  }

  /** Enheten som venter på nytt punkt, eller `null`. (til modus-stripa og test) */
  get flytterEnhet(): string | null { return this.pendingFlytt; }

  /**
   * Flytt en enhet til et punkt. Samme vei som et musedrag ender i: `endretMs`
   * oppdateres, `onMoved` meldes med FRA og TIL (så ordretilbudet kan komme), og
   * settet kringkastes. Ett sted som muterer posisjonen, ikke to.
   */
  flyttTil(id: string, lon: number, lat: number): boolean {
    if (id === BFT_SELF_ID) return false; // en måling kan ikke flyttes
    const u = this.units.get(id);
    if (!u) return false;
    const fra = { lon: u.lon, lat: u.lat };
    if (fra.lon === lon && fra.lat === lat) return false;
    u.lon = lon;
    u.lat = lat;
    u.endretMs = Date.now();
    this.render();
    this.onMoved?.(u, fra, { lon, lat });
    this.onChange();
    return true;
  }

  private onMapClick = (e: MapMouseEvent) => {
    // Flytting FØRST: de to ventetilstandene er gjensidig utelukkende (hver sin
    // kartmodus), men rekkefølgen skal ikke være avhengig av det.
    if (this.pendingFlytt) {
      const id = this.pendingFlytt;
      this.avbrytFlytting();
      this.flyttTil(id, e.lngLat.lng, e.lngLat.lat);
      return;
    }
    if (!this.pending) return;
    const p = this.pending;
    this.disarm();
    this.placeAt(e.lngLat.lng, e.lngLat.lat, p.sidc, p.label, p.orbatId);
  };

  /**
   * Plasser en enhet direkte på et punkt (f.eks. fra høyreklikk-menyen).
   * `orbatId` gis når enheten er koblet til ORBAT — da ER id-en ORBAT-unit-id-en,
   * slik at kartet og ORBAT snakker om SAMME enhet (fase 2). Uten den faller vi
   * tilbake til en løpenummer-id for løse markeringer.
   */
  placeAt(lon: number, lat: number, sidc: string, label: string, orbatId?: string): PlacedUnit {
    const id = orbatId?.trim() || `u${++seq}`;
    ensureSymbol(this.map, sidc);
    const u: PlacedUnit = { id, sidc, lon, lat, label, endretMs: Date.now() };
    this.units.set(id, u);
    this.render();
    this.onChange();
    return u;
  }

  /**
   * Ta MINE EGNE plasserte enheter tilbake etter en reload.
   *
   * ⚠ HVA SOM VAR GALT (eierfunn 2026-08-14, og det alvorligste i den
   * revisjonen): hubben replayer forfatter-settene ved tilkobling, også ditt
   * eget, og klienten la det i `sharedByAuthor` — altså det IKKE-INTERAKTIVE
   * laget. Etter F5 var dine egne enheter derfor ikke dragbare, ikke klikkbare,
   * ikke i Objekter-lista, ikke slettbare — og, verst av alt, de FALT UT AV
   * DANGER-CLOSE-SJEKKEN og av eksporten, fordi begge leser `snapshot()`.
   * Fareavstands-sjekken sluttet altså å telle operatørens egne folk, uten et
   * eneste varsel.
   *
   * Enhetene tas derfor inn i MITT sett igjen. `endretMs` beholdes slik den var,
   * for «sist endret» skal si når enheten faktisk ble flyttet — ikke når siden
   * ble lastet på nytt.
   *
   * Kringkaster IKKE på nytt (`onChange` kalles ikke): settet kom nettopp FRA
   * hubben, og et ekko ville bare bekreftet det vi alt har fått.
   *
   * Returnerer antall gjenopptatte enheter.
   */
  gjenoppta(list: PlacedUnit[]): number {
    let antall = 0;
    for (const u of list) {
      if (!u?.id || typeof u.lon !== 'number' || typeof u.lat !== 'number') continue;
      // BFT-markøren gjenopptas ALDRI: den er en MÅLING, og en gammel måling
      // skal ikke gjenoppstå som «egen posisjon» ved en reload. Den kommer
      // tilbake av seg selv ved neste GPS-fiks hvis BFT er på.
      if (u.id === BFT_SELF_ID) continue;
      ensureSymbol(this.map, u.sidc);
      this.units.set(u.id, { ...u });
      // Fjern den fra det delte laget, ellers ligger den to steder samtidig.
      for (const [forfatter, sett] of this.sharedByAuthor) {
        const rest = sett.filter((s) => s.id !== u.id);
        if (rest.length !== sett.length) this.sharedByAuthor.set(forfatter, rest);
      }
      antall += 1;
    }
    if (antall) {
      this.render();
      this.renderSharedMerged();
    }
    return antall;
  }

  /**
   * Endre SYMBOL (og evt. navn) på en enhet som allerede står på kartet.
   *
   * ⚠ HVA SOM GIKK GALT: en enhet som fikk feil symbol kunne bare SLETTES.
   * Høyreklikk ga navnefelt + Slett, ingenting mer. «Rett symbolet» betydde i
   * praksis: slett, åpne paletten, klikk på kartet igjen — og da fikk enheten ny
   * id. Var den koblet til ORBAT (id = ORBAT-unit-id), var koblingen borte, og
   * kartet og ORBAT snakket ikke lenger om samme enhet. Derfor MUTERER vi her:
   * samme id, samme posisjon, nytt symbol.
   *
   * Returnerer false hvis enheten ikke finnes eller ikke kan endres (BFT), slik
   * at kalleren kan si det til operatøren i stedet for å tie.
   */
  setSidc(id: string, sidc: string, label?: string): boolean {
    if (id === BFT_SELF_ID) return false; // en måling har ikke en «type» å velge
    const u = this.units.get(id);
    if (!u) return false;
    ensureSymbol(this.map, sidc);
    u.sidc = sidc;
    // Navnet følger bare med når det er oppgitt: en enhet operatøren har døpt
    // «Lag 2 Bravo» skal ikke få tilbake katalognavnet «Infanteri» fordi han
    // rettet symbolet.
    if (label !== undefined && label.trim()) u.label = label.trim();
    u.endretMs = Date.now(); // symbolretting ER en endring — «sist endret» skal si det
    this.render();
    this.onChange();
    return true;
  }

  /** Enheten med `id`, hvis den finnes (til popup/palett-forhåndsutfylling). */
  get(id: string): PlacedUnit | undefined { return this.units.get(id); }

  /** BFT: sett/oppdater egen posisjon som en vennlig enhet (fast id → oppdaterer
   * i stedet for å samle duplikater). Deles via samme kanal som andre enheter.
   *
   * `tsMs` er MÅLETIDSPUNKTET og er ikke valgfritt i praksis: uten det kan ingen
   * si om markøren er fersk, og da lyver den. Etiketten får «· BFT» slik at den
   * ikke kan forveksles med en enhet operatøren selv har plassert. */
  setSelf(lon: number, lat: number, label: string, tsMs: number = Date.now()) {
    const id = BFT_SELF_ID;
    const sidc = 'SFGPU-----H----'; // vennlig bakke-enhet
    ensureSymbol(this.map, sidc);
    this.units.set(id, { id, sidc, lon, lat, label: `${label} · BFT`, tsMs });
    this.render();
    this.onChange();
  }

  /**
   * Fjern BFT-markøren (når deling slås av).
   *
   * ⚠ HVA SOM GIKK GALT: markøren ble liggende. Den sto som et permanent
   * enhetsobjekt i kartet og i Objekter-panelet etter at operatøren hadde slått
   * BFT AV — og verre: `units`-settet KRINGKASTES ved hver `onChange`, havner i
   * CoT/GeoJSON-eksport og teller som en egen styrke i danger-close-sjekken.
   * «BFT av — posisjon deles ikke lenger» var altså ikke sant; siste kjente
   * posisjon ble delt videre, i det uendelige, uten alder. Å slå av deling MÅ
   * stoppe delingen.
   *
   * Returnerer true hvis en markør faktisk ble fjernet.
   */
  clearSelf(): boolean {
    if (!this.units.delete(BFT_SELF_ID)) return false;
    this.render();
    this.onChange();
    return true;
  }

  /**
   * Tegn enhetslaget på nytt uten at data er endret.
   * Trengs fordi ALDER går videre av seg selv: stopper GPS-fiksene, kommer ingen
   * `setSelf`, og etiketten ville stått fersk for alltid. Kalleren tikker.
   */
  oppfrisk() { this.render(); }

  /** Fly til og velg en enhet (fra objektlisten). */
  focus(id: string) {
    const u = this.units.get(id);
    if (!u) return;
    this.map.flyTo({ center: [u.lon, u.lat], zoom: 15 });
    this.select(u);
  }

  /**
   * Kalles når en enhet er FLYTTET (dratt til ny posisjon), med både gammel og
   * ny posisjon. FRA-punktet må meldes her fordi det ellers går tapt — draget
   * muterer enheten fortløpende.
   *
   * ⚠ DETTE ER EN MELDING OM EN HENDELSE, IKKE EN ORDRE OM Å SKRIVE EN ORDRE.
   * Tidligere skrev kalleren en fempunktsordre for forflytningen automatisk her.
   * En ordre om forflytning er en konsekvensrik handling: den navngir enhet,
   * rute, tempo og ildåpningsbestemmelse, og er ment å leses opp på samband.
   * Terskelen på {@link FLYTT_TERSKEL_PX} fjernet fumleklikkene, men en reell
   * dragning — f.eks. å rette opp en enhet som var sluppet 30 m feil — genererte
   * fortsatt en ferdig ordre operatøren aldri hadde bedt om. Kalleren skal derfor
   * TILBY ordren og la operatøren velge. Se `onGiOrdre` for veien tilbake.
   */
  onMoved?: (unit: PlacedUnit, fra: { lon: number; lat: number }, til: { lon: number; lat: number }) => void;

  /**
   * «Endre type/symbol …» valgt i enhetens meny. Kalleren åpner enhetspaletten
   * forhåndsutfylt og kaller `setSidc` med SAMME id (se `setSidc`).
   */
  onEndreType?: (unit: PlacedUnit) => void;

  /**
   * «Gi ordre …» valgt i enhetens meny — det EKSPLISITTE valget som erstatter
   * den automatiske ordren etter en dragning. Kalleren eier grunnlaget (siste
   * registrerte forflytning) og avgjør hva ordren skal handle om.
   */
  onGiOrdre?: (unit: PlacedUnit) => void;

  /**
   * Kan det gis ordre for denne enheten nå? Uten grunnlag (ingen registrert
   * forflytning) skal menyvalget være DEAKTIVERT med en forklaring — ikke en
   * knapp som ser ut som den virker og så gir en ordre fra A til A.
   */
  harOrdregrunnlag?: (unit: PlacedUnit) => boolean;

  /**
   * Slå opp enheten i styrkestrukturen. Kalleren eier ORBAT-katalogen.
   *
   * Popup-en avgjorde før ORBAT-kobling med et regex på id-formen
   * (`/^u\d+$/` → «løs markering»). Det er en GJETNING: den kan bare si at
   * id-en ser ORBAT-aktig ut, ikke at enheten faktisk finnes i strukturen — og
   * en id som pekte på et lag som var slettet i ORBAT ble vist som «koblet».
   * Med et reelt oppslag viser kortet nivå og kommandokjede, som er det
   * operatøren trenger for å se at han har rett enhet.
   */
  orbatOppslag?: (id: string) => { navn: string; echelonNavn: string; stiTekst: string } | null;

  /**
   * En enhet er VALGT (kartklikk uten dra, eller objektlista via `focus`).
   * UX-funn 3/5: valget skal fylle det faste detaljpanelet — kalleren (main)
   * eier panelet og bygger innholdet med `enhetDetaljer`. Popup-en her forblir
   * hurtigREDIGERINGEN (navn/symbol/ordre); «se» og «endre» er to ulike ting.
   */
  onVelg?: (unit: PlacedUnit) => void;

  /** Posisjonen enheten sto på da draget startet. */
  private dragFra: { lon: number; lat: number } | null = null;

  private onDown = (e: MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
    if (this.pending) return; // vi plasserer, ikke dra
    // Måler eller tegner operatøren, skal et enhetssymbol under pekeren ikke
    // kapre draget — ellers flytter en måling over en enhet enheten i stedet.
    if (this.annenEier()) return;
    const id = e.features?.[0]?.properties?.uid as string | undefined;
    if (!id) return;
    // ⚠ EN MÅLING KAN IKKE FLYTTES (eierfunn 2026-08-14). BFT-markøren er egen
    // GPS-posisjon. Uten denne sperra kunne den dras med musa: posisjonen ble
    // mutert, `endretMs` satt — og punktet kringkastet videre med det GAMLE
    // `tsMs` intakt. Altså en håndflyttet posisjon som fortsatt utgir seg for å
    // være målt, i strid med regelen om at målingens kilde og alder er udelelig
    // fra verdien. Klikk (uten dra) skal fortsatt virke, så vi returnerer FØR
    // draget settes opp, ikke før hele håndtereren.
    if (id === BFT_SELF_ID) {
      const bft = this.units.get(id);
      if (bft) this.select(bft); // klikk = vis målekortet, aldri flytt
      return;
    }
    e.preventDefault();
    this.dragging = id;
    this.moved = false;
    this.dragStartPx = { x: e.point.x, y: e.point.y };
    const start = this.units.get(id);
    this.dragFra = start ? { lon: start.lon, lat: start.lat } : null;
    this.map.dragPan.disable();
    this.map.on('mousemove', this.onMove);
    this.map.once('mouseup', this.onUp);
  };
  private onMove = (e: MapMouseEvent) => {
    if (!this.dragging) return;
    // `moved` settes bare når pekeren har flyttet seg MER enn terskelen. Før ble
    // den satt av den første mousemove, og en skjelvende hånd gjorde et klikk om
    // til en forflytningsordre. Se FLYTT_TERSKEL_PX.
    const start = this.dragStartPx;
    if (!start) this.moved = true;
    else if (Math.hypot(e.point.x - start.x, e.point.y - start.y) >= FLYTT_TERSKEL_PX) this.moved = true;
    if (!this.moved) return; // små bevegelser skal ikke rikke symbolet i det hele tatt
    const u = this.units.get(this.dragging);
    if (!u) return;
    u.lon = e.lngLat.lng;
    u.lat = e.lngLat.lat;
    this.render();
  };
  private onUp = () => {
    const id = this.dragging;
    this.dragging = null;
    this.map.off('mousemove', this.onMove);
    this.map.dragPan.enable();
    this.map.getCanvas().style.cursor = '';
    if (!id) return;
    if (this.moved) {
      // Meld forflytningen med FRA og TIL, så ordregangen kan starte (fase 3).
      const u = this.units.get(id);
      const fra = this.dragFra;
      if (u && fra && (fra.lon !== u.lon || fra.lat !== u.lat)) {
        u.endretMs = Date.now(); // en dragning er en endring av situasjonsbildet
        this.onMoved?.(u, fra, { lon: u.lon, lat: u.lat });
      }
      this.onChange();
    } else { const u = this.units.get(id); if (u) this.select(u); } // klikk uten dra = velg
    this.dragFra = null;
    this.dragStartPx = null;
  };

  private select(u: PlacedUnit) {
    // Detaljpanelet FØRST (funn 3/5): et valg skal alltid synes i den faste
    // flata, uansett om popup-en under lukkes med neste kartklikk.
    this.onVelg?.(u);
    this.popup?.remove();
    const box = document.createElement('div');
    box.className = 'unit-popup';
    const title = document.createElement('div');
    title.className = 'unit-popup-title';
    title.textContent = u.label;
    box.append(title);

    // ── EGEN posisjon (BFT) er en MÅLING, ikke en enhet ────────────────────
    // ⚠ Markøren lå tidligere i samme kort som alt annet: navnefelt, Slett — som
    // om operatøren hadde plassert seg selv på kartet. Da ser en GPS-måling ut
    // som en enhet noen står inne for. Kortet er derfor lesbart, ikke redigerbart,
    // og det sier alltid alderen på målingen.
    if (u.id === BFT_SELF_ID) {
      const m = document.createElement('div');
      m.className = 'unit-popup-meta';
      m.textContent = 'EGEN POSISJON (BFT) — måling fra GPS';
      const alder = document.createElement('div');
      alder.className = 'unit-popup-meta';
      alder.textContent = u.tsMs === undefined ? 'Måletidspunkt ukjent' : maalingsLinje(u.tsMs, Date.now());
      const note = document.createElement('div');
      note.className = 'unit-popup-note';
      note.textContent = 'Slå av BFT for å slutte å dele posisjonen — markøren fjernes samtidig.';
      box.append(m, alder, note);
      this.popup = new maplibregl.Popup({ closeButton: true, closeOnClick: true, offset: 18 })
        .setLngLat([u.lon, u.lat]).setDOMContent(box).addTo(this.map);
      return;
    }

    // ORBAT-kobling synlig: en operatør må kunne se OM enheten er koblet, ellers
    // er «koblet til ORBAT» bare en påstand i koden.
    const meta = document.createElement('div');
    meta.className = 'unit-popup-meta';
    const orbat = this.orbatOppslag?.(u.id) ?? null;
    if (orbat) {
      meta.textContent = `${orbat.echelonNavn} · ${orbat.stiTekst}`;
      meta.title = `ORBAT-id: ${u.id}`;
    } else {
      meta.textContent = 'Ikke ORBAT-koblet (løs markering)';
      meta.title = 'Enheten står bare på kartet. Plasser den fra ORBAT-velgeren '
        + 'for å koble den til styrkestrukturen — da kan ordre bruke riktig nivå.';
    }

    // Inline navneendring — IKKE window.prompt (forbudt mønster i UX-QA: blokkerer
    // hele vinduet, kan ikke stiles, og oppfører seg ulikt i pop-out-vinduer).
    const rad = document.createElement('div');
    rad.className = 'unit-popup-rename';
    const felt = document.createElement('input');
    felt.className = 'unit-popup-input';
    felt.value = u.label;
    felt.setAttribute('aria-label', 'Navn på enheten');
    const lagre = () => {
      const n = felt.value.trim();
      if (n && n !== u.label) { u.label = n; u.endretMs = Date.now(); this.render(); this.onChange(); }
      this.popup?.remove();
    };
    felt.onkeydown = (ev) => {
      if (ev.key === 'Enter') { ev.preventDefault(); lagre(); }
      if (ev.key === 'Escape') { ev.preventDefault(); this.popup?.remove(); }
    };
    rad.append(felt, mkBtn('Lagre navn', lagre));
    box.append(meta, rad);

    // Begge menyvalgene under gjør noe ANNET enn å plassere. Da må en armert
    // plassering slippes — ellers ville neste kartklikk slippe en enhet
    // operatøren for lengst hadde gått bort fra. Presis samme fellen `arm()`
    // beskriver, og veien inn finnes: objektlista kaller `focus()` → `select()`
    // uten å bry seg om at noe er armert. `disarm()` er idempotent og frigir
    // kartmodusen, så vi bygger på arbitreringen i stedet for å omgå den.
    const byttOppgave = () => { this.disarm(); this.popup?.remove(); };

    // Rett symbolet i stedet for å slette enheten — se `setSidc`.
    box.append(mkBtn('Endre type/symbol …', () => {
      byttOppgave();
      this.onEndreType?.(u);
    }));

    // Ordren ligger bak et eksplisitt valg (før: den kom av seg selv ved dragning).
    const ordre = mkBtn('Gi ordre …', () => {
      byttOppgave();
      this.onGiOrdre?.(u);
    });
    if (this.onGiOrdre && this.harOrdregrunnlag?.(u) === false) {
      ordre.disabled = true;
      ordre.title = 'Ingen forflytning registrert. Dra enheten til ny posisjon — '
        + 'da tilbys en forflytningsordre for strekningen.';
    }
    if (this.onGiOrdre) box.append(ordre);

    const del = mkBtn('Ta av kartet', () => { this.remove(u.id); this.popup?.remove(); });
    del.classList.add('danger');
    del.title = orbat
      ? 'Fjerner markeringen fra kartet. Enheten blir stående i ORBAT.'
      : 'Fjerner markeringen fra kartet.';
    box.append(del);
    this.popup = new maplibregl.Popup({ closeButton: true, closeOnClick: true, offset: 18 })
      .setLngLat([u.lon, u.lat]).setDOMContent(box).addTo(this.map);
  }

  /**
   * Ta enheten AV kartet. Returnerer false hvis den ikke sto der.
   *
   * Å fjerne en enhet fra kartet sier bare at vi ikke lenger vet — eller ikke
   * lenger viser — hvor den er. ORBAT røres IKKE: laget finnes fortsatt i
   * styrkestrukturen og kan plasseres på nytt. Sletting av selve enheten hører
   * hjemme i ORBAT-panelet, som fører hendelseslogg for det.
   *
   * Fantes bare som en knapp inne i popup-en før, så ingen annen vei inn (ORBAT-
   * velger, kommandopalett, liste) kunne ta en enhet av kartet.
   */
  remove(id: string): boolean {
    if (!this.units.delete(id)) return false;
    this.render();
    this.onChange();
    return true;
  }

  clear() {
    this.units.clear();
    this.render();
    this.onChange();
  }
  count() {
    return this.units.size;
  }
  /** Delbart øyeblikksbilde av egne enheter (til sanntidsdeling). */
  snapshot(): PlacedUnit[] {
    return [...this.units.values()];
  }
  /**
   * Delte enheter per FORFATTER — siste sett per forfatter vinner, akkurat som
   * whiteboardet. Uten dette klobber to samtidige operatører hverandre, og
   * hub-replayen (ett event per forfatter ved tilkobling) ville bare vist den
   * siste forfatterens enheter. Tomt sett = forfatteren har fjernet sine.
   */
  private sharedByAuthor = new Map<string, PlacedUnit[]>();

  /** Tegn en forfatters delte enheter (slås sammen med de øvrige forfatternes). */
  renderShared(from: string, units: PlacedUnit[]) {
    if (units.length) this.sharedByAuthor.set(from, units);
    else this.sharedByAuthor.delete(from);
    this.renderSharedMerged();
  }

  /** Delte enheter fra alle forfattere, slått sammen. */
  sharedSnapshot(): PlacedUnit[] {
    return [...this.sharedByAuthor.values()].flat();
  }

  private renderSharedMerged() {
    if (!this.ready) return;
    const merged = this.sharedSnapshot();
    for (const u of merged) ensureSymbol(this.map, u.sidc);
    (this.map.getSource('units-shared') as GeoJSONSource)?.setData(featureCollection(toFeatures(merged)) as never);
  }

  private render() {
    if (!this.ready) return;
    (this.map.getSource('units') as GeoJSONSource)?.setData(
      featureCollection(toFeatures([...this.units.values()])) as never,
    );
  }
}

function toFeatures(units: PlacedUnit[], nowMs = Date.now()): GeoFeature[] {
  return units.map((u) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [u.lon, u.lat] },
    // `label` er VISNINGSetiketten (kan bære alders-varsel), `navn` er det rene
    // navnet — så en fremtidig konsument ikke leser «⚠ 4 min GAMMEL» som del av
    // enhetens navn.
    properties: { uid: u.id, sidc: u.sidc, label: kartEtikett(u, nowMs), navn: u.label },
  }));
}
function mkBtn(text: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'unit-popup-btn';
  b.textContent = text;
  b.onclick = onClick;
  return b;
}
